/**
 * The focused group's active tab — the tab the context object describes.
 * Pure, so the recorder and the hook agree by construction.
 */
import type { PaneGroup, WorkspaceTab } from '@features/workspace/types';

/** The focused group and its active tab (null when the active id is stale). */
export interface FocusedTab {
  group: PaneGroup;
  tab: WorkspaceTab | null;
}

/**
 * Resolve the focused tab. Mirrors `useWorkspaceNav`: focus can only rest on the
 * secondary group while its pane is visible; otherwise it is coerced to primary
 * read-side (the stored preference is never rewritten).
 */
export function focusedTab(
  tabs: WorkspaceTab[],
  primaryActiveTabId: string | null,
  secondaryActiveTabId: string | null,
  storedFocusedGroup: PaneGroup,
  secondaryVisible: boolean
): FocusedTab {
  const group: PaneGroup = secondaryVisible ? storedFocusedGroup : 'primary';
  const id = group === 'primary' ? primaryActiveTabId : secondaryActiveTabId;
  return { group, tab: tabs.find(t => t.id === id && t.group === group) ?? null };
}

/**
 * Identity of a focused tab for stamping a selection: group + tab id + path, so
 * a path change, an active-tab change or a focused-group change each produce a
 * new key — the spec's three invalidations, as one string comparison.
 */
export function tabKey({ group, tab }: FocusedTab): string {
  return tab ? `${group}:${tab.id}:${tab.path}` : `${group}:none`;
}
